import { useEffect, useMemo, useState } from "react";
import { addDays, addMonths, endOfMonth, endOfWeek, format, startOfMonth, startOfWeek, subDays, subMonths } from "date-fns";
import { ChevronLeft, ChevronRight, Trophy, Users, CalendarCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { EVENT_TYPES, posterFor } from "../lib/calendar";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import EventDialog from "../components/EventDialog";
import { MonthView, WeekView, DayView } from "../components/calendar/Views";
import { usePageMeta } from "../hooks/usePageMeta";
import { useEventStructuredData } from "../hooks/useEventStructuredData";

export default function CalendarPage() {
  usePageMeta(
    null,
    "Discover upcoming TCG events at Hex Labs Games in Maldon, Essex. Pokémon tournaments, prereleases, league events, One Piece, Riftbound, Lorcana, MTG and more."
  );
  const [view, setView] = useState("month");
  const [cursor, setCursor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [stats, setStats] = useState(null);
  useEventStructuredData(events);

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => setEvents([]));
    api.get("/stats/overview").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const visible = useMemo(
    () => (filter === "all" ? events : events.filter((e) => e.event_type === filter)),
    [events, filter]
  );

  const step = (dir) => {
    if (view === "month") setCursor((c) => (dir > 0 ? addMonths(c, 1) : subMonths(c, 1)));
    else if (view === "week") setCursor((c) => (dir > 0 ? addDays(c, 7) : subDays(c, 7)));
    else setCursor((c) => (dir > 0 ? addDays(c, 1) : subDays(c, 1)));
  };

  const label =
    view === "month"
      ? format(cursor, "MMMM yyyy")
      : view === "week"
      ? `${format(startOfWeek(cursor, { weekStartsOn: 1 }), "d MMM")} – ${format(endOfWeek(cursor, { weekStartsOn: 1 }), "d MMM yyyy")}`
      : format(cursor, "d MMMM yyyy");

  const nextUp = useMemo(
    () => events.filter((e) => new Date(e.start) >= new Date()).slice(0, 3),
    [events]
  );

  return (
    <div className="mx-auto max-w-7xl px-5 lg:px-10 py-10 lg:py-16">
      <section className="mb-12 lg:mb-20">
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-primary mb-4">Hex Labs Games · Maldon</p>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter max-w-3xl">
          TOURNAMENT<br />
          <span className="text-primary">CALENDAR</span> & RANKS
        </h1>
        <p className="mt-5 text-base md:text-lg text-neutral-400 max-w-xl">
          Every Pokémon League Challenge, Cup and Friendly in one place. Grab your ticket, add it to your
          calendar, then climb the leaderboard.
        </p>

        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/leaderboard" data-testid="hero-leaderboard-btn">
            <Button className="bg-primary text-black font-bold uppercase tracking-wider rounded-full hover:bg-[#FFA94D] hover:scale-[1.03] transition-transform duration-300">
              <Trophy className="h-4 w-4 mr-2" /> View Leaderboards
            </Button>
          </Link>
          <Link to="/results" data-testid="hero-results-btn">
            <Button variant="outline" className="rounded-full border-white/20 hover:border-primary hover:text-primary transition-colors duration-200">
              Past Results
            </Button>
          </Link>
        </div>

        {stats && (
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="stats-strip">
            {[
              { label: "Upcoming events", value: stats.upcoming_events, icon: CalendarCheck },
              { label: "Ranked tournaments", value: stats.ranked_tournaments, icon: Trophy },
              { label: "Ranked players", value: stats.players, icon: Users },
              { label: "Prerelease logged", value: stats.prerelease_tournaments, icon: CalendarCheck },
            ].map((s) => (
              <div key={s.label} className="border border-white/10 rounded-md p-4 bg-white/[0.02]">
                <s.icon className="h-4 w-4 text-primary mb-2" />
                <div className="font-mono text-2xl">{s.value}</div>
                <div className="text-[11px] uppercase tracking-wider text-neutral-500">{s.label}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      {nextUp.length > 0 && (
        <section className="mb-14">
          <h2 className="text-base md:text-lg font-display mb-4 text-neutral-300">Next up</h2>
          <div className="grid gap-3 sm:grid-cols-3">
            {nextUp.map((e) => (
              <button
                key={e.id}
                data-testid={`next-up-${e.id}`}
                onClick={() => setSelected(e)}
                className="group text-left border border-white/10 rounded-md bg-white/[0.02] overflow-hidden hover:border-primary/60 hover:scale-[1.02] transition-transform duration-300"
              >
                <div className="relative h-36 overflow-hidden">
                  <img
                    src={posterFor(e)}
                    alt={e.title}
                    data-testid={`next-up-poster-${e.id}`}
                    className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
                  {e.sold_out && (
                    <span className="absolute top-2 right-2 px-2 py-0.5 rounded-sm bg-destructive text-white text-[10px] font-mono font-bold uppercase tracking-[0.15em]">
                      Sold out
                    </span>
                  )}
                  <span className="absolute bottom-2 left-3 font-mono text-[11px] text-primary">
                    {format(new Date(e.start), "EEE d MMM · HH:mm")}
                  </span>
                </div>
                <div className="p-4">
                  <div className="font-display text-lg tracking-tight">{e.title}</div>
                  <div className="text-[11px] text-neutral-500 mt-1">{e.game}</div>
                </div>
              </button>
            ))}
          </div>
          <div className="mt-3 flex justify-end">
            <Link
              to="/events"
              data-testid="view-all-events-link"
              className="text-[11px] uppercase tracking-[0.2em] text-neutral-500 hover:text-primary transition-colors duration-200"
            >
              View all events →
            </Link>
          </div>
        </section>
      )}

      <section>
        <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between mb-6">
          <div className="flex items-center gap-2">
            <Button data-testid="cal-prev" size="icon" variant="outline" className="rounded-full border-white/20" onClick={() => step(-1)}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button data-testid="cal-next" size="icon" variant="outline" className="rounded-full border-white/20" onClick={() => step(1)}>
              <ChevronRight className="h-4 w-4" />
            </Button>
            <h2 className="font-display text-xl sm:text-2xl tracking-tighter ml-2" data-testid="cal-label">{label}</h2>
            <Button data-testid="cal-today" size="sm" variant="ghost" className="text-neutral-400 hover:text-primary" onClick={() => setCursor(new Date())}>
              Today
            </Button>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Tabs value={view} onValueChange={setView}>
              <TabsList className="bg-white/5 border border-white/10 rounded-full">
                {["month", "week", "day"].map((v) => (
                  <TabsTrigger
                    key={v}
                    value={v}
                    data-testid={`view-${v}`}
                    className="rounded-full data-[state=active]:bg-primary data-[state=active]:text-black text-xs uppercase tracking-wider px-4"
                  >
                    {v}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            <div className="flex flex-wrap gap-1">
              <button
                data-testid="filter-all"
                onClick={() => setFilter("all")}
                className={`text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors duration-200 ${
                  filter === "all" ? "border-primary text-primary" : "border-white/10 text-neutral-500 hover:text-white"
                }`}
              >
                All
              </button>
              {EVENT_TYPES.map((t) => (
                <button
                  key={t.value}
                  data-testid={`filter-${t.value}`}
                  onClick={() => setFilter(t.value)}
                  className="text-[11px] uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors duration-200"
                  style={{
                    borderColor: filter === t.value ? t.color : "rgba(255,255,255,0.1)",
                    color: filter === t.value ? t.color : "#737373",
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {view === "month" && <MonthView cursor={cursor} events={visible} onSelect={setSelected} />}
        {view === "week" && <WeekView cursor={cursor} events={visible} onSelect={setSelected} />}
        {view === "day" && <DayView cursor={cursor} events={visible} onSelect={setSelected} />}
      </section>

      <EventDialog event={selected} open={!!selected} onOpenChange={(o) => !o && setSelected(null)} />
    </div>
  );
}
